import { useState } from "react";
import { api, ApiError, type AgreementDocument, type PurchaseOfferReview } from "../../lib/api";
import { usePurchaseOfferRequirements } from "../../lib/usePurchaseOfferRequirements";
import type { PostFlowPreviewModel } from "./usePostFlowPreview";

/** The posting controller retains the exact acknowledged payload through quota
 * or uncertain-result retries. Editing source facts invalidates its review. */
export function useListingPurchaseOffer(model: PostFlowPreviewModel) {
  const enabled = model.type === "item" && model.form.mode === "sale" && model.form.kind !== "business" && model.step >= 4 && model.draft.ready;
  const loaded = usePurchaseOfferRequirements(enabled);
  const [offerKey] = useState(() => crypto.randomUUID());
  const [prepared, setPrepared] = useState<{ document: AgreementDocument; payload: Record<string, unknown>; fingerprint: string } | null>(null);
  const [reviewed, setReviewed] = useState(false), [preparing, setPreparing] = useState(false), [uncertain, setUncertain] = useState(false);
  const source = (value: string) => value === "description" || value === "productInformation" || value === "schedule" ? value : undefined;
  const review: PurchaseOfferReview = { offerKey, inspectionSource: source(model.form.purchaseInspectionSource), inspectionSchedule: model.form.purchaseInspectionSchedule || undefined,
    propertySource: source(model.form.purchasePropertySource), propertySchedule: model.form.purchasePropertySchedule || undefined };
  const payload = { ...model.buildPayload(), title: model.form.title.trim(), purchaseReview: review }, fingerprint = JSON.stringify(payload);
  const current = prepared?.fingerprint === fingerprint;
  const canPublish = !enabled || !!loaded.requirements && (!loaded.requirements.active || current && reviewed);
  async function prepare() {
    if (preparing || model.reviewError) return;
    setPreparing(true); model.setError(null); setReviewed(false);
    try {
      const result = await api.previewPurchaseListing(payload);
      if (!result.document) throw new Error("The selected purchase wording is unavailable. Refresh the wording and retry review.");
      setPrepared({ document: result.document, payload, fingerprint });
    } catch (failure) { model.setError(failure instanceof Error ? failure.message : "Could not prepare the seller offer. Your draft is retained."); }
    finally { setPreparing(false); }
  }
  function publicationPayload(title: string): Record<string, unknown> {
    if (!enabled || loaded.requirements?.active === false) return { ...model.buildPayload(), title };
    if (!canPublish || !prepared || title !== model.form.title.trim()) throw new Error("Review and acknowledge the current seller offer before publishing.");
    return { ...prepared.payload, title, purchaseReview: { ...review, expectedDocumentId: prepared.document.id } };
  }
  function failed(failure: unknown) {
    if (!enabled || !prepared) return;
    if (!(failure instanceof ApiError) || failure.status >= 500) setUncertain(true);
    else if (failure.status === 409) { setPrepared(null); setReviewed(false); loaded.retry(); }
  }
  return { ...model, purchaseOffer: { enabled, loaded, review, document: current ? prepared?.document ?? null : null, reviewed: current && reviewed,
    setReviewed, preparing, uncertain, canPublish, prepare, publicationPayload, failed,
    changeFacts: (value: PurchaseOfferReview) => {
      model.set("purchaseInspectionSource", value.inspectionSource || ""); model.set("purchaseInspectionSchedule", value.inspectionSchedule || "");
      model.set("purchasePropertySource", value.propertySource || ""); model.set("purchasePropertySchedule", value.propertySchedule || "");
    } } };
}
export type ListingPurchaseOfferModel = ReturnType<typeof useListingPurchaseOffer>;
